/**
 * Funzioni PURE della shell del drawer condiviso Attività/Nota, isolate qui per essere
 * testabili senza montare React/TipTap (i moduli della shell importano editor, store e
 * client Supabase). Nessun import: solo logica.
 */

/** Fonte: LeadStatusUpdateDrawer.tsx:847-853 — "adesso + 30 min" arrotondato ai 15 successivi. */
export function defaultStartDateTime(): Date {
  const start = new Date(Date.now() + 30 * 60 * 1000);
  start.setMinutes(Math.ceil(start.getMinutes() / 15) * 15, 0, 0);
  return start;
}

/**
 * Data+ora di partenza di un'attività pre-compilata (`initialActivityData`).
 *
 * `due_date` è `YYYY-MM-DD`; `due_time` è `HH:mm` ed è OPZIONALE — il pre-fill suggerito
 * dall'AI non lo porta, quindi l'assenza ricade sul default storico delle 09:00. Lo porta
 * invece l'escalation dal composer inline della cronologia, dove l'utente ha scelto un'ora
 * esplicita che altrimenti andrebbe persa passando dal composer al drawer.
 *
 * `null` quando manca la data: il chiamante ricade su `defaultStartDateTime()`.
 */
export function composeInitialActivityStart(dueDate?: string, dueTime?: string): Date | null {
  if (!dueDate || !/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) return null;
  const time = dueTime && /^\d{2}:\d{2}$/.test(dueTime) ? dueTime : '09:00';
  const start = new Date(`${dueDate}T${time}:00`);
  return isNaN(start.getTime()) ? null : start;
}

/**
 * L'HTML di TipTap per un editor svuotato è `<p></p>`, non `''`: il `note.trim()` della
 * fonte (:963, :1038) lo considererebbe contenuto valido, salvando una nota vuota e
 * lasciando passare il flusso `requireNote`. Qui si guarda il testo, non il markup.
 */
export function hasNoteContent(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;
}

/**
 * Composizione finale data+ora per `saveActivity` (che riceve `Date` già combinati).
 * Il ROLLOVER DI MEZZANOTTE della fonte (StatusUpdateDrawer.tsx:529-533 — fine < inizio →
 * +1 giorno) resta responsabilità della shell: qui è applicato sulla coppia di `Date`
 * perché il form condiviso non tiene più i campi stringa `due_date`/`due_time`/`end_time`
 * (ActivityTab è controllato su `Date`, review post-Task 8 IMPORTANT 3).
 */
export function composeActivityDates(start: Date, end: Date | null): { startDateTime: Date; endDateTime: Date | null } {
  if (!end) return { startDateTime: start, endDateTime: null };
  const adjusted = new Date(end.getTime());
  if (adjusted.getTime() < start.getTime()) adjusted.setDate(adjusted.getDate() + 1);
  return { startDateTime: start, endDateTime: adjusted };
}


/**
 * Stato iniziale della spunta "Mostra descrizione nell'evento" (bug 31/08/2026:
 * ripartiva sempre spenta anche in edit, e il salvataggio cancellava le note
 * dall'evento). In creazione vale l'ultima scelta dell'utente (`preferita`,
 * accesa la prima volta: CSE 11/09/2026, appuntamenti senza note nell'evento
 * perche' la spunta partiva spenta). In edit si reidrata dal flag persistito
 * in metadata; se assente (righe storiche) lo stato è SCONOSCIUTO e il sync
 * non toccherà la description dell'evento.
 */
export function resolveIncludeDescriptionInit(
  editing: { metadata?: Record<string, any> | null } | null | undefined,
  preferita = true
): { value: boolean; known: boolean } {
  if (!editing) return { value: preferita, known: true };
  const flag = editing.metadata?.include_description_in_event;
  if (typeof flag === 'boolean') return { value: flag, known: true };
  return { value: false, known: false };
}

/** Ultima scelta dell'utente per la spunta, ricordata su questo browser. */
const CHIAVE_PREFERENZA_DESCRIZIONE = 'flusia-attivita-descrizione-nell-evento';

export function leggiPreferenzaDescrizione(): boolean {
  try {
    return localStorage.getItem(CHIAVE_PREFERENZA_DESCRIZIONE) !== 'no';
  } catch {
    return true;
  }
}

export function ricordaPreferenzaDescrizione(v: boolean): void {
  try {
    localStorage.setItem(CHIAVE_PREFERENZA_DESCRIZIONE, v ? 'si' : 'no');
  } catch {
    // browser senza storage: si riparte da "accesa" la prossima volta
  }
}
